import {useCustomPreference} from './use-custom-preference'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useIntl} from 'react-intl'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useShopperContext, useUsid} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

export const useMaxQuantityItems = () => {
    const intl = useIntl()
    const {usid} = useUsid()
    const {site} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    const {data: shopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )
    const {data: basket} = useCurrentBasket()
    const employeeBalancePrefData = useCustomPreference('EmployeeProgram_Data')
    const corporateFamilyLimits = useCustomPreference('CorporateSale_Limits')
    const standardFamilyLimits = useCustomPreference('StandardSale_Limits')

    const cartItems = basket?.productItems || []

    const isEmployee = customer?.c_isEmployee
    const isCorporate = shopperContext?.customQualifiers?.isCorporate === 'true'

    const getMaxQuantity = (product) => {
        if (isEmployee) {
            return Number(employeeBalancePrefData?.maxItemQuantity) || null
        }
        const familyId = product?.c_materialGroup || product?.c_MaterialGroup
        let familyLimits = isCorporate
            ? corporateFamilyLimits?.products || {}
            : standardFamilyLimits?.products || {}
        return familyId && familyLimits[familyId] ? Number(familyLimits[familyId]) : null
    }

    const getFamilyQtyInCart = (familyId) => {
        if (!familyId) return 0
        return cartItems
            .filter(
                (item) => item?.c_MaterialGroup === familyId || item?.c_materialGroup === familyId,
            )
            .reduce((sum, item) => sum + (item.quantity || 0), 0)
    }

    const isQuantityAllowed = (product, toAddQty = 0, totalQuantity = 0) => {
        if (isEmployee) {
            const maxQuantity = Number(employeeBalancePrefData?.maxItemQuantity)
            const currentQty = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
            if (!maxQuantity) return true
            const allowed = totalQuantity
                ? totalQuantity <= maxQuantity
                : currentQty + toAddQty <= maxQuantity
            return allowed
        }

        const familyId = product?.c_MaterialGroup || product?.c_materialGroup
        let familyLimits = isCorporate
            ? corporateFamilyLimits?.products || {}
            : standardFamilyLimits?.products || {}

        if (familyId && familyLimits && familyLimits[familyId]) {
            const maxFamilyQty = Number(familyLimits[familyId])
            const currentFamilyQty = getFamilyQtyInCart(familyId)

            if (totalQuantity) {
                return totalQuantity <= maxFamilyQty
            }
            return currentFamilyQty + toAddQty <= maxFamilyQty
        }

        return true
    }

    const getMaxQuantityErrorMessage = (max) =>
        intl.formatMessage(
            {
                id: 'cart.error.max_item_quantity',
                defaultMessage: 'You can add a maximum of {max} items to the cart.',
            },
            {max},
        )

    return {isQuantityAllowed, getMaxQuantityErrorMessage, getMaxQuantity}
}
